import React, { useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  NumberInput,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconBuildingFactory2,
  IconCalendarEvent,
  IconCheck,
  IconChecks,
  IconClipboardList,
  IconCoin,
  IconFileDescription,
  IconInfoCircle,
  IconMailForward,
  IconPlus,
  IconReceipt2,
  IconSearch,
  IconSend,
  IconTrash,
  IconTruckDelivery,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

const approvedRequestsSeed = [
  {
    id: 'PR-2026-0105',
    title: 'Yeni personel için çalışma ekipmanı talebi',
    department: 'Bilgi İşlem',
    requester: 'Muhammed Selvi',
    needByDate: '2026-03-25',
    status: 'APPROVED',
    priority: 'HIGH',
    totalAmount: 44300,
    costCenter: 'Merkez Ofis / BT',
    approvedAt: '2026-03-18 11:30',
    approvedBy: 'Ahmet Demir',
    lines: [
      { id: 'PRL-1', lineNo: 1, description: 'Dell Latitude 15 Laptop', quantity: 1, uom: 'Adet', estimatedUnitPrice: 42000 },
      { id: 'PRL-2', lineNo: 2, description: 'Laptop Çantası', quantity: 1, uom: 'Adet', estimatedUnitPrice: 1750 },
      { id: 'PRL-3', lineNo: 3, description: 'Kablosuz Mouse', quantity: 1, uom: 'Adet', estimatedUnitPrice: 550 },
    ],
  },
  {
    id: 'PR-2026-0108',
    title: 'Aylık kırtasiye ve sarf malzeme alımı',
    department: 'İdari İşler',
    requester: 'Ayşe Kaya',
    needByDate: '2026-03-23',
    status: 'APPROVED',
    priority: 'MEDIUM',
    totalAmount: 12650,
    costCenter: 'Genel Yönetim',
    approvedAt: '2026-03-18 09:10',
    approvedBy: 'Can Yılmaz',
    lines: [
      { id: 'PRL-4', lineNo: 1, description: 'A4 Fotokopi Kağıdı', quantity: 20, uom: 'Koli', estimatedUnitPrice: 450 },
      { id: 'PRL-5', lineNo: 2, description: 'Tükenmez Kalem', quantity: 50, uom: 'Adet', estimatedUnitPrice: 18 },
    ],
  },
];

const vendorOptions = [
  { value: 'Anadolu Endüstri', label: 'Anadolu Endüstri' },
  { value: 'OfisDonanım Ltd.', label: 'OfisDonanım Ltd.' },
  { value: 'Tekno Tedarik A.Ş.', label: 'Tekno Tedarik A.Ş.' },
  { value: 'Kurumsal Market', label: 'Kurumsal Market' },
];

const currencyOptions = [
  { value: 'TRY', label: 'TRY' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

const quoteStatusOptions = [
  { value: 'DRAFT', label: 'Taslak' },
  { value: 'SUBMITTED', label: 'Kaydedildi' },
  { value: 'SELECTED', label: 'Seçildi' },
  { value: 'REJECTED', label: 'Reddedildi' },
];

const comparisonSeed = [
  {
    vendor: 'Tekno Tedarik A.Ş.',
    totalAmount: 100200,
    leadTimeDays: 5,
    paymentTerms: 'Peşin',
    status: 'SUBMITTED',
  },
  {
    vendor: 'OfisDonanım Ltd.',
    totalAmount: 98500,
    leadTimeDays: 4,
    paymentTerms: '15 gün vade',
    status: 'DRAFT',
  },
];

const currencyFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  maximumFractionDigits: 2,
});

const statusConfig = {
  APPROVED: { label: 'Onaylandı', color: 'teal' },
  DRAFT: { label: 'Taslak', color: 'gray' },
  SUBMITTED: { label: 'Kaydedildi', color: 'blue' },
  SELECTED: { label: 'Seçildi', color: 'green' },
  REJECTED: { label: 'Reddedildi', color: 'red' },
  HIGH: { label: 'Yüksek', color: 'orange' },
  MEDIUM: { label: 'Orta', color: 'blue' },
};

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || { label: status, color: 'gray' };
  return (
    <Badge variant="light" color={cfg.color} radius="sm">
      {cfg.label}
    </Badge>
  );
}

function SummaryCard({ title, value, subtitle, color, icon: Icon }) {
  return (
    <Card withBorder radius="lg" p="lg" shadow="sm">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text size="sm" c="dimmed" fw={500}>{title}</Text>
          <Text fw={800} size="1.55rem" lh={1.1}>{value}</Text>
          <Text size="xs" c="dimmed">{subtitle}</Text>
        </Stack>
        <ThemeIcon color={color} variant="light" size={46} radius="md">
          <Icon size={22} />
        </ThemeIcon>
      </Group>
    </Card>
  );
}

function SectionCard({ title, description, children, action }) {
  return (
    <Paper withBorder radius="lg" p="lg" shadow="sm">
      <Group justify="space-between" align="flex-start" mb="md">
        <div>
          <Title order={4}>{title}</Title>
          {description ? <Text size="sm" c="dimmed" mt={4}>{description}</Text> : null}
        </div>
        {action}
      </Group>
      {children}
    </Paper>
  );
}

function InfoRow({ label, value }) {
  return (
    <>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text size="sm" c="dimmed">{label}</Text>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Text size="sm" fw={600} ta="right">{value || '-'}</Text>
        ) : value}
      </Group>
      <Divider />
    </>
  );
}

export default function ProcurementQuoteEntryPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedRequestId, setSelectedRequestId] = useState(approvedRequestsSeed[0]?.id || '');
  const [quoteHeader, setQuoteHeader] = useState({
    quoteNo: 'Q-2026-0041',
    vendorName: 'OfisDonanım Ltd.',
    currency: 'TRY',
    status: 'DRAFT',
    validUntil: '2026-03-22',
    leadTimeDays: 4,
    paymentTerms: '15 gün vade',
    note: 'Kurulum ve teslimat dahil fiyat kurgusu planlandı.',
  });

  const [quoteLines, setQuoteLines] = useState([
    {
      id: 'QL-1',
      requisitionLineId: 'PRL-1',
      description: 'Dell Latitude 15 Laptop',
      quantity: 1,
      uom: 'Adet',
      unitPrice: 42000,
    },
    {
      id: 'QL-2',
      requisitionLineId: 'PRL-2',
      description: 'Laptop Çantası',
      quantity: 1,
      uom: 'Adet',
      unitPrice: 1750,
    },
    {
      id: 'QL-3',
      requisitionLineId: 'PRL-3',
      description: 'Kablosuz Mouse',
      quantity: 1,
      uom: 'Adet',
      unitPrice: 550,
    },
    {
      id: 'QL-4',
      requisitionLineId: null,
      description: 'Kurulum ve teslimat',
      quantity: 1,
      uom: 'Hizmet',
      unitPrice: 9900,
    },
  ]);

  const filteredRequests = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return approvedRequestsSeed;
    return approvedRequestsSeed.filter((item) => JSON.stringify(item).toLowerCase().includes(q));
  }, [search]);

  const selectedRequest = useMemo(() => {
    return approvedRequestsSeed.find((item) => item.id === selectedRequestId) || approvedRequestsSeed[0] || null;
  }, [selectedRequestId]);

  const totals = useMemo(() => {
    const totalAmount = quoteLines.reduce((acc, line) => acc + Number(line.quantity || 0) * Number(line.unitPrice || 0), 0);
    const lineCount = quoteLines.length;
    return { totalAmount, lineCount };
  }, [quoteLines]);

  const updateHeader = (field, value) => {
    setQuoteHeader((prev) => ({ ...prev, [field]: value }));
  };

  const updateLine = (id, field, value) => {
    setQuoteLines((prev) => prev.map((line) => (line.id === id ? { ...line, [field]: value } : line)));
  };

  const addFreeLine = () => {
    setQuoteLines((prev) => [
      ...prev,
      {
        id: `QL-${prev.length + 1}`,
        requisitionLineId: null,
        description: '',
        quantity: 1,
        uom: 'Hizmet',
        unitPrice: 0,
      },
    ]);
  };

  const removeLine = (id) => {
    setQuoteLines((prev) => prev.filter((line) => line.id !== id));
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Paper
          withBorder
          radius="xl"
          p="xl"
          shadow="sm"
          style={{
            background: 'linear-gradient(135deg, rgba(245,247,250,1) 0%, rgba(255,255,255,1) 100%)',
          }}
        >
          <Group justify="space-between" align="flex-start" gap="xl">
            <Stack gap="sm" maw={860}>
              <Group gap="sm">
                <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} px={0} onClick={() => navigate("/purchase-dashboard")}>
                  Geri Dön
                </Button>
                <Badge variant="light" color="teal" leftSection={<IconReceipt2 size={14} />}>
                  Statik demo teklif giriş ekranı
                </Badge>
              </Group>
              <div>
                <Title order={1}>Teklif Giriş / Teklif Toplama</Title>
                <Text size="sm" c="dimmed" mt="sm">
                  Onaylanan talepler için tedarikçi tekliflerinin girildiği ekran. Bu sayfada teklif başlığı,
                  satır bazlı fiyatlar, termin, ödeme koşulları ve kıyas alanları birlikte yönetilir.
                </Text>
              </div>
            </Stack>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, xl: 4 }} spacing="md" mt="xl">
            <SummaryCard title="Onaylı Talep" value={approvedRequestsSeed.length} subtitle="Teklife açılabilecek kayıt" color="teal" icon={IconChecks} />
            <SummaryCard title="Aktif Teklif No" value={quoteHeader.quoteNo} subtitle="Demo oluşturulan belge" color="blue" icon={IconFileDescription} />
            <SummaryCard title="Toplam Teklif" value={currencyFormatter.format(totals.totalAmount)} subtitle="Satır bazlı fiyat toplamı" color="orange" icon={IconCoin} />
            <SummaryCard title="Geçerlilik" value={quoteHeader.validUntil} subtitle="Teklif son geçerlilik tarihi" color="grape" icon={IconCalendarEvent} />
          </SimpleGrid>
        </Paper>

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, xl: 3.5 }}>
            <Stack gap="md">
              <SectionCard title="Onaylı Talepler" description="Teklif girişi yapılacak talebi seçin.">
                <Stack gap="sm">
                  <TextInput
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    placeholder="Talep no, başlık, departman ara..."
                    leftSection={<IconSearch size={16} />}
                  />

                  <Stack gap="xs">
                    {filteredRequests.map((item) => (
                      <Paper
                        key={item.id}
                        withBorder
                        radius="md"
                        p="sm"
                        onClick={() => setSelectedRequestId(item.id)}
                        style={{
                          cursor: 'pointer',
                          borderColor: selectedRequestId === item.id ? 'var(--mantine-color-teal-4)' : undefined,
                          backgroundColor: selectedRequestId === item.id ? 'var(--mantine-color-teal-0)' : undefined,
                        }}
                      >
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <div>
                            <Text fw={700} size="sm">{item.id}</Text>
                            <Text size="sm" mt={2}>{item.title}</Text>
                            <Text size="xs" c="dimmed" mt={4}>{item.department} • {item.requester}</Text>
                          </div>
                          <Stack gap={6} align="flex-end">
                            <StatusBadge status={item.status} />
                            <Badge variant="light" color={statusConfig[item.priority]?.color || 'gray'}>
                              {statusConfig[item.priority]?.label || item.priority}
                            </Badge>
                          </Stack>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </Stack>
              </SectionCard>

              <SectionCard title="Süreç Akışı" description="Bu ekranın süreç içindeki yeri.">
                <Stack gap="sm">
                  <Paper withBorder radius="md" p="sm">
                    <Group wrap="nowrap" align="flex-start">
                      <ThemeIcon color="blue" variant="light" mt={2}><IconFileDescription size={16} /></ThemeIcon>
                      <div>
                        <Text size="sm" fw={700}>1. Talep Oluşturuldu</Text>
                        <Text size="xs" c="dimmed" mt={2}>İhtiyaç ve kalemler talep ekranında oluşturuldu.</Text>
                      </div>
                    </Group>
                  </Paper>
                  <Paper withBorder radius="md" p="sm">
                    <Group wrap="nowrap" align="flex-start">
                      <ThemeIcon color="teal" variant="light" mt={2}><IconChecks size={16} /></ThemeIcon>
                      <div>
                        <Text size="sm" fw={700}>2. Talep Onaylandı</Text>
                        <Text size="xs" c="dimmed" mt={2}>İlgili onay mercileri tamamlandı ve talep teklif sürecine açıldı.</Text>
                      </div>
                    </Group>
                  </Paper>
                  <Paper withBorder radius="md" p="sm">
                    <Group wrap="nowrap" align="flex-start">
                      <ThemeIcon color="orange" variant="light" mt={2}><IconBuildingFactory2 size={16} /></ThemeIcon>
                      <div>
                        <Text size="sm" fw={700}>3. Teklif Toplanıyor</Text>
                        <Text size="xs" c="dimmed" mt={2}>Bu sayfada tedarikçi teklifleri kaydedilir ve kıyaslanır.</Text>
                      </div>
                    </Group>
                  </Paper>
                </Stack>
              </SectionCard>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, xl: 8.5 }}>
            <Stack gap="lg">
              {selectedRequest && (
                <>
                  <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="md">
                    <SectionCard title="Onaylı Talep Özeti" description="Teklif girişine baz olacak talep bilgileri.">
                      <Stack gap="sm">
                        <InfoRow label="Talep No" value={selectedRequest.id} />
                        <InfoRow label="Başlık" value={selectedRequest.title} />
                        <InfoRow label="Departman" value={selectedRequest.department} />
                        <InfoRow label="Talep Eden" value={selectedRequest.requester} />
                        <InfoRow label="Onaylayan" value={selectedRequest.approvedBy} />
                        <InfoRow label="Onay Tarihi" value={selectedRequest.approvedAt} />
                        <InfoRow label="Masraf Merkezi" value={selectedRequest.costCenter} />
                        <InfoRow label="İhtiyaç Tarihi" value={selectedRequest.needByDate} />
                        <InfoRow label="Tahmini Tutar" value={currencyFormatter.format(selectedRequest.totalAmount)} />
                      </Stack>
                    </SectionCard>

                    <SectionCard title="Teklif Başlık Bilgileri" description="Tedarikçi ve teklif üst alanları.">
                      <Grid gutter="md">
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <TextInput
                            label="Teklif No"
                            value={quoteHeader.quoteNo}
                            onChange={(event) => updateHeader('quoteNo', event.currentTarget.value)}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <Select
                            label="Tedarikçi"
                            data={vendorOptions}
                            value={quoteHeader.vendorName}
                            onChange={(value) => updateHeader('vendorName', value || '')}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                          <Select
                            label="Para Birimi"
                            data={currencyOptions}
                            value={quoteHeader.currency}
                            onChange={(value) => updateHeader('currency', value || 'TRY')}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                          <Select
                            label="Durum"
                            data={quoteStatusOptions}
                            value={quoteHeader.status}
                            onChange={(value) => updateHeader('status', value || 'DRAFT')}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 4 }}>
                          <TextInput
                            label="Geçerlilik Tarihi"
                            type="date"
                            value={quoteHeader.validUntil}
                            onChange={(event) => updateHeader('validUntil', event.currentTarget.value)}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <NumberInput
                            label="Termin (Gün)"
                            value={quoteHeader.leadTimeDays}
                            min={0}
                            onChange={(value) => updateHeader('leadTimeDays', Number(value) || 0)}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <TextInput
                            label="Ödeme Koşulu"
                            value={quoteHeader.paymentTerms}
                            onChange={(event) => updateHeader('paymentTerms', event.currentTarget.value)}
                          />
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <Textarea
                            label="Teklif Notu"
                            minRows={3}
                            value={quoteHeader.note}
                            onChange={(event) => updateHeader('note', event.currentTarget.value)}
                          />
                        </Grid.Col>
                      </Grid>
                    </SectionCard>
                  </SimpleGrid>

                  <SectionCard
                    title="Teklif Satırları"
                    description="Talep kalemlerine karşılık fiyat girin, gerekirse serbest hizmet satırı ekleyin."
                    action={<Button leftSection={<IconPlus size={16} />} onClick={addFreeLine}>Serbest Satır Ekle</Button>}
                  >
                    <Stack gap="md">
                      {quoteLines.map((line, index) => (
                        <Paper key={line.id} withBorder radius="md" p="md">
                          <Stack gap="md">
                            <Group justify="space-between" align="center">
                              <Group gap="xs">
                                <ThemeIcon color="blue" variant="light" radius="md"><IconClipboardList size={16} /></ThemeIcon>
                                <div>
                                  <Text fw={700}>Teklif Satırı {index + 1}</Text>
                                  <Text size="xs" c="dimmed">{line.requisitionLineId ? `Talep satırı: ${line.requisitionLineId}` : 'Serbest ek satır'}</Text>
                                </div>
                              </Group>
                              <ActionIcon color="red" variant="light" onClick={() => removeLine(line.id)}>
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Group>

                            <Grid gutter="md">
                              <Grid.Col span={{ base: 12, md: 5 }}>
                                <TextInput
                                  label="Açıklama"
                                  value={line.description}
                                  onChange={(event) => updateLine(line.id, 'description', event.currentTarget.value)}
                                />
                              </Grid.Col>
                              <Grid.Col span={{ base: 12, md: 2 }}>
                                <NumberInput
                                  label="Miktar"
                                  value={line.quantity}
                                  min={0}
                                  onChange={(value) => updateLine(line.id, 'quantity', Number(value) || 0)}
                                />
                              </Grid.Col>
                              <Grid.Col span={{ base: 12, md: 2 }}>
                                <TextInput
                                  label="Birim"
                                  value={line.uom}
                                  onChange={(event) => updateLine(line.id, 'uom', event.currentTarget.value)}
                                />
                              </Grid.Col>
                              <Grid.Col span={{ base: 12, md: 3 }}>
                                <NumberInput
                                  label="Birim Fiyat"
                                  value={line.unitPrice}
                                  min={0}
                                  thousandSeparator="."
                                  decimalSeparator="," 
                                  onChange={(value) => updateLine(line.id, 'unitPrice', Number(value) || 0)}
                                />
                              </Grid.Col>
                              <Grid.Col span={12}>
                                <Paper radius="md" p="sm" bg="gray.0">
                                  <Group justify="space-between">
                                    <Text size="sm" c="dimmed">Satır Tutarı</Text>
                                    <Text fw={700}>{currencyFormatter.format(Number(line.quantity || 0) * Number(line.unitPrice || 0))}</Text>
                                  </Group>
                                </Paper>
                              </Grid.Col>
                            </Grid>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </SectionCard>

                  <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="md">
                    <SectionCard title="Talep Kalem Referansı" description="Teklif girilen satırların dayandığı talep kalemleri.">
                      <ScrollArea>
                        <Table verticalSpacing="sm" horizontalSpacing="md" striped>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>#</Table.Th>
                              <Table.Th>Açıklama</Table.Th>
                              <Table.Th>Miktar</Table.Th>
                              <Table.Th>Birim</Table.Th>
                              <Table.Th>Tahmini</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {selectedRequest.lines.map((line) => (
                              <Table.Tr key={line.id}>
                                <Table.Td>{line.lineNo}</Table.Td>
                                <Table.Td>{line.description}</Table.Td>
                                <Table.Td>{line.quantity}</Table.Td>
                                <Table.Td>{line.uom}</Table.Td>
                                <Table.Td><Text fw={600}>{currencyFormatter.format(line.estimatedUnitPrice)}</Text></Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                    </SectionCard>

                    <SectionCard title="Teklif Ön İzleme" description="Kaydedilecek teklif başlığı ve toplam görünümü.">
                      <Stack gap="sm">
                        <InfoRow label="Teklif No" value={quoteHeader.quoteNo} />
                        <InfoRow label="Tedarikçi" value={quoteHeader.vendorName} />
                        <InfoRow label="Para Birimi" value={quoteHeader.currency} />
                        <InfoRow label="Durum" value={<StatusBadge status={quoteHeader.status} />} />
                        <InfoRow label="Geçerlilik" value={quoteHeader.validUntil} />
                        <InfoRow label="Termin" value={`${quoteHeader.leadTimeDays} gün`} />
                        <InfoRow label="Ödeme Koşulu" value={quoteHeader.paymentTerms} />
                        <InfoRow label="Toplam Satır" value={totals.lineCount} />
                        <InfoRow label="Teklif Toplamı" value={currencyFormatter.format(totals.totalAmount)} />
                      </Stack>
                    </SectionCard>
                  </SimpleGrid>

                  <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="md">
                    <SectionCard title="Teklif Kıyas Alanı" description="Aynı talep için toplanan diğer tekliflerle kıyas görünümü.">
                      <Stack gap="sm">
                        {comparisonSeed.map((item, index) => (
                          <Paper key={`${item.vendor}-${index}`} withBorder radius="md" p="sm">
                            <Group justify="space-between" align="flex-start">
                              <div>
                                <Text fw={700} size="sm">{item.vendor}</Text>
                                <Text size="xs" c="dimmed" mt={4}>
                                  Termin: {item.leadTimeDays} gün • {item.paymentTerms}
                                </Text>
                              </div>
                              <Stack gap={6} align="flex-end">
                                <Text fw={700}>{currencyFormatter.format(item.totalAmount)}</Text>
                                <StatusBadge status={item.status} />
                              </Stack>
                            </Group>
                          </Paper>
                        ))}
                      </Stack>
                    </SectionCard>

                    <SectionCard title="Aksiyonlar" description="Demo amaçlı işlem butonları.">
                      <Stack gap="sm">
                        <Button variant="default" leftSection={<IconCheck size={16} />} fullWidth>
                          Taslak Kaydet
                        </Button>
                        <Button leftSection={<IconSend size={16} />} fullWidth>
                          Teklifi Kaydet
                        </Button>
                        <Button variant="light" color="teal" leftSection={<IconMailForward size={16} />} fullWidth>
                          Talep Sahibine Bildir
                        </Button>
                        <Button variant="light" color="gray" leftSection={<IconTruckDelivery size={16} />} fullWidth>
                          Sonraki Aşamayı Simüle Et
                        </Button>
                      </Stack>
                    </SectionCard>
                  </SimpleGrid>
                </>
              )}
            </Stack>
          </Grid.Col>
        </Grid>

        <Paper withBorder radius="lg" p="md" shadow="sm" style={{ borderStyle: 'dashed' }}>
          <Group align="flex-start" wrap="nowrap">
            <ThemeIcon color="blue" variant="light" mt={2}><IconInfoCircle size={16} /></ThemeIcon>
            <Text size="sm" c="dimmed">
              Sonraki mantıklı ekran, tekliflerin kıyaslanıp bir tanesinin seçildiği “Teklif Değerlendirme / Teklif Seçim Ekranı” olur.
              Ardından mal kabul ve fatura adımına geçebiliriz.
            </Text>
          </Group>
        </Paper>
      </Stack>
    </Container>
  );
}
